import React, { useState, useEffect, useRef, CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Settings as GearIcon, ChevronLeft, Check, X } from 'lucide-react';
import { RootState } from '../../store';
import logger from '../../utils/logger';
import {
  setSettingsOpen,
  setPlaybackSpeed,
  setSelectedQuality,
  setAutoQuality,
} from '../../store/slices/playerSlice';

interface SettingsProps {
  isMobile: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
}

const PLAYBACK_SPEEDS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

export const Settings: React.FC<SettingsProps> = ({ isMobile, containerRef }): JSX.Element => {
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dispatch = useDispatch();
  const {
    settingsOpen,
    playbackSpeed,
    selectedQuality,
    autoQuality,
    availableQualities,
    currentActiveQuality,
  } = useSelector((state: RootState) => state.player);

  const [currentMenu, setCurrentMenu] = useState<'main' | 'speed' | 'quality'>('main');
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const [hasOpened, setHasOpened] = useState(false);

  // Function to calculate menu position
  const calculateMenuPosition = () => {
    if (!buttonRef.current || !menuRef.current || isMobile) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const menuRect = menuRef.current.getBoundingClientRect();
    const containerRect = containerRef.current?.getBoundingClientRect();
    const isFullscreen = document.fullscreenElement === containerRef.current;

    logger.debug('Menu position calculation:', {
      buttonRect: {
        top: buttonRect.top,
        right: buttonRect.right,
        bottom: buttonRect.bottom,
        left: buttonRect.left
      },
      menuRect: {
        height: menuRect.height,
        width: menuRect.width
      },
      containerRect: containerRect ? {
        top: containerRect.top,
        height: containerRect.height,
        width: containerRect.width
      } : null,
      isFullscreen
    });

    if (isFullscreen && containerRect) {
      const relativeTop = buttonRect.top - containerRect.top;
      const spaceAbove = relativeTop;
      const spaceBelow = containerRect.height - relativeTop;
      
      const newPosition = {
        top: Math.max(10, relativeTop - menuRect.height - 10),
        right: containerRect.width - (buttonRect.right - containerRect.left) + 10
      };

      logger.debug('Calculated fullscreen position:', {
        relativeTop,
        spaceAbove,
        spaceBelow,
        newPosition
      });
      
      setMenuPosition(newPosition);
    } else {
      const newPosition = {
        top: Math.max(10, buttonRect.top - menuRect.height - 10),
        right: window.innerWidth - buttonRect.right + 10
      };

      logger.debug('Calculated window position:', newPosition);
      
      setMenuPosition(newPosition);
    }
  };

  // Update position when menu content changes
  useEffect(() => {
    if (settingsOpen && !isMobile) {
      logger.debug('Menu content changed, updating position', {
        currentMenu,
        settingsOpen,
        hasOpened
      });
      
      // Calculate position immediately for initial render
      calculateMenuPosition();
      
      // And after a frame to account for content changes
      requestAnimationFrame(() => {
        calculateMenuPosition();
      });
    }
  }, [settingsOpen, currentMenu, isMobile]);

  // Update position on window resize
  useEffect(() => {
    if (!settingsOpen || isMobile) return;

    const handleResize = () => {
      calculateMenuPosition();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [settingsOpen, isMobile]);

  // Reset menu when settings are closed
  useEffect(() => {
    if (!settingsOpen) {
      setCurrentMenu('main');
    }
  }, [settingsOpen]);

  // Reset hasOpened when menu closes
  useEffect(() => {
    if (!settingsOpen) {
      setHasOpened(false);
    }
  }, [settingsOpen]);

  // Add logging
  useEffect(() => {
    if (menuRef.current) {
      const menuRect = menuRef.current.getBoundingClientRect();
      const parentRect = menuRef.current.parentElement?.getBoundingClientRect();
      
      logger.debug('Settings menu state:', {
        settingsOpen,
        isMobile,
        menuBounds: {
          top: menuRect.top,
          right: menuRect.right,
          bottom: menuRect.bottom,
          left: menuRect.left,
          height: menuRect.height,
          width: menuRect.width
        },
        parentBounds: parentRect ? {
          top: parentRect.top,
          right: parentRect.right,
          bottom: parentRect.bottom,
          left: parentRect.left,
          height: parentRect.height,
          width: parentRect.width
        } : null,
        windowDimensions: {
          width: window.innerWidth,
          height: window.innerHeight
        }
      });
    }
  }, [settingsOpen, isMobile]);

  // Handle click outside for mobile
  useEffect(() => {
    if (!isMobile || !settingsOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        dispatch(setSettingsOpen(false));
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobile, settingsOpen, dispatch]);

  // Handle touch events for mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation();
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    e.stopPropagation();
  };

  const handleClose = () => {
    dispatch(setSettingsOpen(false));
    setCurrentMenu('main');
  };

  const handleSpeedChange = (speed: number) => {
    dispatch(setPlaybackSpeed(speed));
  };

  const handleQualityChange = (quality: string) => {
    logger.debug('Quality change requested:', {
      newQuality: quality,
      previousQuality: selectedQuality,
      wasAuto: autoQuality
    });
    
    dispatch(setSelectedQuality(quality));
    dispatch(setAutoQuality(quality === 'auto'));
  };

  const renderMainMenu = () => (
    <div className="space-y-2">
      <button
        onClick={() => setCurrentMenu('speed')}
        className="w-full flex items-center justify-between p-3 hover:bg-gray-700/50 rounded-lg"
      >
        <span>Playback Speed</span>
        <span className="text-gray-400">{playbackSpeed}x</span>
      </button>
      <button
        onClick={() => setCurrentMenu('quality')}
        className="w-full flex items-center justify-between p-3 hover:bg-gray-700/50 rounded-lg"
      >
        <span>Quality</span>
        <span className="text-gray-400">
          {autoQuality 
            ? currentActiveQuality 
              ? `Auto (${currentActiveQuality})`
              : 'Auto'
            : selectedQuality}
        </span>
      </button>
    </div>
  );

  const renderSpeedMenu = () => (
    <div className="space-y-2">
      {PLAYBACK_SPEEDS.map((speed) => (
        <button
          key={speed}
          onClick={() => handleSpeedChange(speed)}
          className="w-full flex items-center justify-between p-3 hover:bg-gray-700/50 rounded-lg"
        >
          <span>{speed}x</span>
          {speed === playbackSpeed && <Check size={20} className="text-primary" />}
        </button>
      ))}
    </div>
  );

  const renderQualityMenu = () => {
    logger.debug('Quality menu state:', {
      availableQualities,
      selectedQuality,
      autoQuality,
      currentMenu,
      currentActiveQuality
    });
    
    return (
    <div className="space-y-2">
      <button
        onClick={() => handleQualityChange('auto')}
        className="w-full flex items-center justify-between p-3 hover:bg-gray-700/50 rounded-lg"
      >
        <span>
          {autoQuality && currentActiveQuality
            ? `Auto (${currentActiveQuality})`
            : 'Auto'}
        </span>
        {autoQuality && <Check size={20} className="text-primary" />}
      </button>
      {availableQualities.map((quality: string) => (
        <button
          key={quality}
          onClick={() => handleQualityChange(quality)}
          className="w-full flex items-center justify-between p-3 hover:bg-gray-700/50 rounded-lg"
        >
          <span>{quality}</span>
          {quality === selectedQuality && !autoQuality && (
            <Check size={20} className="text-primary" />
          )}
        </button>
      ))}
    </div>
  )};

  // Add logging for animation states
  useEffect(() => {
    logger.debug('Settings animation state:', {
      isMobile,
      settingsOpen,
      hasOpened,
      currentMenu,
      transitionClasses: {
        shouldAnimate: isMobile || hasOpened,
        menuOpen: settingsOpen,
        currentTransform: settingsOpen ? 'translate-y-0' : `${isMobile ? 'translate-y-full' : 'translate-x-8'}`
      }
    });
  }, [isMobile, settingsOpen, hasOpened, currentMenu]);

  // Menu content
  const renderMenu = (): JSX.Element => {
    const isFullscreen = document.fullscreenElement === containerRef.current;
    
    // Pre-calculate styles with proper typing
    const menuStyles: CSSProperties | undefined = !isMobile ? {
      position: isFullscreen ? 'absolute' : 'fixed',
      top: `${menuPosition.top}px`,
      right: `${menuPosition.right}px`,
      maxHeight: isFullscreen ? 'calc(100vh - 20px)' : '80vh',
      overflowY: 'auto',
      visibility: settingsOpen ? 'visible' : 'hidden',
      transform: 'translate3d(0, 0, 0)',
      backfaceVisibility: 'hidden',
      willChange: 'transform'
    } : undefined;

    return (
      <div
        ref={menuRef}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        style={menuStyles}
        className={`bg-black/90 text-white ${
          isMobile
            ? 'fixed bottom-0 left-0 right-0 rounded-t-xl max-h-[80vh] overflow-y-auto'
            : 'w-72 rounded-xl shadow-lg'
        } ${
          !isMobile && !hasOpened ? 'no-transition' : 'transition-transform duration-200 ease-out'
        } ${
          settingsOpen 
            ? 'opacity-100 translate-y-0 pointer-events-auto' 
            : `opacity-0 ${isMobile ? 'translate-y-full' : 'translate-y-2'} pointer-events-none`
        } z-[9999]`}
        onTransitionEnd={() => {
          if (settingsOpen && !hasOpened) {
            setHasOpened(true);
            logger.debug('Menu transition ended, hasOpened set to true');
          }
        }}
      >
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              {currentMenu !== 'main' && (
                <button
                  onClick={() => setCurrentMenu('main')}
                  className="mr-2 hover:text-gray-300 transition-colors"
                >
                  <ChevronLeft size={24} />
                </button>
              )}
              <h2 className="text-lg font-semibold">
                {currentMenu === 'main'
                  ? 'Settings'
                  : currentMenu === 'speed'
                  ? 'Playback Speed'
                  : 'Quality'}
              </h2>
            </div>
            <button
              onClick={handleClose}
              className="hover:text-gray-300 transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          <div className={`transform transition-opacity duration-200 ${
            settingsOpen ? 'opacity-100' : 'opacity-0'
          }`}>
            {currentMenu === 'main' && renderMainMenu()}
            {currentMenu === 'speed' && renderSpeedMenu()}
            {currentMenu === 'quality' && renderQualityMenu()}
          </div>
        </div>
      </div>
    );
  };

  // Settings button
  const renderButton = () => (
    <button
      ref={buttonRef}
      onClick={() => {
        dispatch(setSettingsOpen(true));
      }}
      onTouchEnd={(e) => {
        e.preventDefault();
        e.stopPropagation();
        dispatch(setSettingsOpen(true));
      }}
      className="text-white hover:text-gray-200"
      title="Settings"
    >
      <GearIcon size={24} />
    </button>
  );

  return (
    <>
      {renderButton()}
      {settingsOpen && containerRef.current && createPortal(
        <>
          <div 
            className={`${!isMobile ? 'fixed' : ''} inset-0 bg-black/50 transition-opacity duration-200 ${
              settingsOpen ? 'opacity-100' : 'opacity-0'
            } z-[9998]`}
            onClick={handleClose}
          />
          {renderMenu()}
        </>,
        document.fullscreenElement === containerRef.current
          ? containerRef.current
          : document.body
      )}
    </>
  );
}; 